import { useState } from 'react'
import { Wordmark } from '../components/Wordmark'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ApiError,
  discoverEnterprise,
  enterpriseStartUrl,
  googleStartUrl,
  loginWithServiceKey,
  startEmailSignIn,
  verifyEmailSignIn,
  type Session,
} from '../api'
import { ThemeToggle } from '../components/ThemeToggle'
import { SIGN_IN_ERRORS, signInErrorMessage } from '../signinErrors'
import { TermsBox } from './AcceptTerms'

type Offers = Extract<Session, { authenticated: false }>['sign_in'] & { terms_url?: string }


/**
 * The operator form: the service key, exchanged for a session. It is for when
 * the usual way in is not available rather than for everyday use, which is
 * why it sits collapsed beneath the other ways in -- and why it is the one
 * form still offered on a deployment that is closed to the public.
 */
export function OperatorSignIn({ onSignedIn, autoFocus }: { onSignedIn: () => void; autoFocus?: boolean }) {
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await loginWithServiceKey(secret)
      onSignedIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.')
      setSecret('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <label htmlFor="service-key">Service key</label>
      <input
        id="service-key"
        type="password"
        autoFocus={autoFocus}
        autoComplete="current-password"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
      />
      {error && <div className="notice notice-bad signin-notice">{error}</div>}
      {/* A stable hook: the browser harnesses sign in this way, and there is
          more than one submit button on this card. */}
      <button id="operator-sign-in" className="btn btn-block signin-submit" type="submit" disabled={busy || !secret}>
        {busy ? <><span className="spinner" /> Signing in…</> : 'Sign in with the service key'}
      </button>
      <p className="small faint signin-hint">
        The operator credential, for when the usual sign-in is unavailable. The server prints a generated key on startup when{' '}
        <code>DOCSDONE_SERVICE_KEY</code> is unset.
      </p>
    </form>
  )
}

/**
 * What the app shows when it could not ask the server who is signed in.
 *
 * It used to show the operator form. Nothing was known about which ways in
 * the server offers, so the page offered the one that is always there; but
 * that form talks to the same server, so it could not work either, and a
 * person with no signal was shown a password field for operators. This says
 * what went wrong and asks again, and offers no form, because none can work.
 *
 * A server that answered with a sentence of its own is not unreachable, so
 * that sentence is shown. Anything else (no answer at all, or an answer that
 * is not the API's, such as an error page from in front of it) is the app's
 * sentence for an unreachable server.
 */
export function CannotReach({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const answered = error instanceof ApiError && error.code !== 'unknown'
  return (
    <div className="signin-wrap">
      <div id="cannot-reach" className="card signin-card">
        <div className="signin-logo">
          <Wordmark />
        </div>
        <ThemeToggle />
        <h1>{answered ? 'docsdone could not answer' : 'Could not reach docsdone'}</h1>
        <p className="small muted">
          {answered ? error.message : 'Check your connection and try again.'}
        </p>
        <button id="try-again" className="btn btn-block signin-submit" type="button" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  )
}

/**
 * A sign-in again that came back as somebody else, and could not be signed
 * out. That person's pages are not shown, because at /account/delete they are
 * their own deletion page with a fresh sign-in, and the sign-in page cannot be
 * shown over a session that is still open. Try again signs it out again.
 */
export function CouldNotSignOut({ refused, onRetry }: { refused: string; onRetry: () => void }) {
  return (
    <div className="signin-wrap">
      <div id="signin-again-stuck" className="card signin-card">
        <div className="signin-logo">
          <Wordmark />
        </div>
        <ThemeToggle />
        <h1>Sign in again</h1>
        <div className="notice notice-bad signin-notice">{refused}</div>
        <p className="small muted">That account could not be signed out. Try again to sign it out.</p>
        <button id="try-again" className="btn btn-block signin-submit" type="button" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  )
}

/**
 * The sentence above a sign-in that has to be as one account: the deletion
 * page's "Sign in again". The app's Reauthenticate screen says the same.
 */
function signInAgainLead(address: string): string {
  return `Sign in as ${address} to show it is still you. Then you have ten minutes to delete your account.`
}

/**
 * SignIn gates the sender side of the product.
 *
 * It exists because the sender endpoints apply the platform signing key: an
 * unauthenticated caller could otherwise obtain our seal over a document they
 * wrote themselves, which would make every signature docsdone issues worthless.
 * The signing link is deliberately not behind this — a recipient is not a user
 * of ours and arrives holding only their link.
 *
 * What is shown depends on what the server offers. A code to an email address
 * is the way in that needs an account nowhere else, so it leads; Google sits
 * beside it for the companies that use it; the operator form stays collapsed
 * beneath both, because it is for when the usual way in is unavailable rather
 * than for everyday use.
 *
 * A company that signs its staff in through its own identity provider gets no
 * button of its own, because there is nothing to put on one: the page cannot
 * know which company somebody belongs to until they say. The address is what
 * decides, so the one field already on the page is what asks.
 */
export function SignIn({ offers, onSignedIn, again, againRefused, onLeaveAgain }: {
  offers: Offers
  onSignedIn: () => void
  /**
   * Why the last sign-in was signed out again: it was somebody other than
   * the person the deletion page asked to sign in again.
   */
  againRefused?: string
  /** Leaves the sign-in again for an ordinary sign-in, deleting nothing. */
  onLeaveAgain?: () => void
  /**
   * The address to sign in as, when the deletion page sent the person here to
   * sign in again. The code form is then fixed to it, as the app's is: the
   * page used to show the ordinary sign-in, so a different address could be
   * typed by mistake, and nothing said why the person was there.
   */
  again?: string
}) {
  const location = useLocation()

  // Three routes land on /signin?error=<code>: the Google callback, the
  // enterprise callback, and the enterprise start when it refuses before
  // sending anybody anywhere. Anywhere else, a successful sign-in should bring
  // the person back to where they were.
  const params = new URLSearchParams(location.search)
  const callbackError = signInErrorMessage(params.get('error'))
  const returnTo = location.pathname === '/signin' ? '/' : location.pathname + location.search

  const operatorForm = (
    <OperatorSignIn onSignedIn={onSignedIn} autoFocus={!offers.google && !offers.email && !offers.enterprise} />
  )

  return (
    <div className="signin-wrap">
      <div className="card signin-card">
        <div className="signin-logo">
          <Wordmark />
        </div>
        <ThemeToggle />
        <h1>{again ? 'Sign in again' : 'Sign in'}</h1>
        <p className="small muted" id="signin-lead">
          {again
            ? signInAgainLead(again)
            : location.pathname === '/connect'
              ? 'A chat wants to connect to your account.'
              : 'Sign in to send and manage documents.'}
        </p>
        {callbackError && <div className="notice notice-bad signin-notice">{callbackError}</div>}
        {again && againRefused && <div id="signin-again-refused" className="notice notice-bad signin-notice">{againRefused}</div>}
        {(offers.email || offers.enterprise) && (
          <EmailSignIn
            onSignedIn={onSignedIn}
            autoFocus={!offers.google}
            code={offers.email}
            enterprise={offers.enterprise}
            returnTo={returnTo}
            termsUrl={offers.terms_url}
            fixedAddress={again}
          />
        )}
        {(offers.email || offers.enterprise) && offers.google && <p className="signin-or small faint">or</p>}
        {offers.google && (
          <button
            id="continue-with-google"
            className="btn btn-block btn-secondary"
            type="button"
            onClick={() => window.location.assign(googleStartUrl(returnTo))}
          >
            Continue with Google
          </button>
        )}
        {offers.service && (offers.google || offers.email || offers.enterprise) && (
          <details className="signin-operator">
            <summary className="small muted">Operator sign-in</summary>
            <div className="signin-operator-body">{operatorForm}</div>
          </details>
        )}
        {offers.service && !offers.google && !offers.email && !offers.enterprise && operatorForm}
        {again && onLeaveAgain && (
          <button id="signin-leave-again" className="btn btn-link small signin-leave-again" type="button" onClick={onLeaveAgain}>
            Cancel and sign in as usual
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Signing in with a six-digit code, or at the company's own identity provider.
 *
 * One field decides between the two, because the address is the only thing
 * that can decide: the domain says which company, and the server answers on
 * the domain alone. The button says "Continue" rather than "Email me a code"
 * while that is still open, because promising a code and delivering a redirect
 * to Okta is a worse surprise than a neutral word.
 *
 * Discovery failing is not an error anybody is shown WHEN A CODE IS ALSO ON
 * offer: it falls back to the code, which is a way in that works, and somebody
 * whose company uses an identity provider must not be stranded because the
 * broker is having a bad minute. On a deployment with a broker and no mailbox
 * there is nothing to fall back TO, so the same failure has to be said out
 * loud rather than silently doing nothing.
 *
 * Two more things here are deliberate and neither is a UI preference.
 *
 * The confirmation never says whether the address can sign in. "A code is on
 * its way" would be a claim; "if that address can sign in, a code is on its
 * way" is the truth, and the server answers identically either way so the page
 * has nothing to branch on even if it wanted to. A form reachable by everybody
 * must not answer questions about who docsdone's customers are.
 *
 * The address is carried into the second step rather than re-typed, but the
 * step is still reversible: somebody who mistyped needs a way back that does
 * not cost them the code they are waiting for.
 */
function EmailSignIn({
  onSignedIn,
  autoFocus,
  code: codeOffered,
  enterprise,
  returnTo,
  termsUrl,
  fixedAddress,
}: {
  onSignedIn: () => void
  autoFocus: boolean
  /** Whether this deployment can send a one-time code at all. It cannot
   * without a mailbox, and the two offers are independent on the server: a
   * deployment may have a broker and no mailer. */
  code: boolean
  enterprise: boolean
  returnTo: string
  /** The terms page the server names, if it names one. */
  termsUrl?: string
  /** An address the person must sign in as: shown, not typed (SignIn's `again`). */
  fixedAddress?: string
}) {
  const navigate = useNavigate()
  const [email, setEmail] = useState(fixedAddress ?? '')
  const [code, setCode] = useState('')
  // Minutes, from the server's own answer. A hardcoded "ten minutes" becomes a
  // lie the moment SignInCodeTTL changes, and nothing would fail.
  const [expiresInMinutes, setExpiresInMinutes] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(false)

  async function ask(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    // The redirect is the one exit from this function that never comes back,
    // so it is the one that must not clear busy. Doing so unwinds the spinner
    // while the navigation is still in flight: the button snaps back to an
    // enabled "Continue", the page looks idle for as long as the broker takes,
    // and a second press starts a second sign-in.
    let redirecting = false
    try {
      if (enterprise) {
        const found = await discoverEnterprise(email)
        if (found.enterprise) {
          redirecting = true
          window.location.assign(enterpriseStartUrl(email, returnTo))
          return
        }
        if (!codeOffered) {
          // Nothing to fall back to. Saying so is the only honest answer, and
          // the sentence is the one the server's own refusal carries.
          setError(SIGN_IN_ERRORS.no_connection)
          return
        }
      }
      const { expires_in_seconds } = await startEmailSignIn(email)
      setExpiresInMinutes(Math.max(1, Math.round(expires_in_seconds / 60)))
      setCode('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not ask for a code.')
    } finally {
      if (!redirecting) setBusy(false)
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const session = await verifyEmailSignIn(email, code, acceptTerms)
      // Proving a second credential in a browser already signed in as somebody
      // else is a question, not a sign-in: the server keeps the session they
      // had and holds the new one until they answer it.
      if (session.authenticated && session.join_offered) {
        navigate(session.join_offered)
      }
      onSignedIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code was not accepted.')
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  if (!expiresInMinutes) {
    return (
      <form onSubmit={ask}>
        {fixedAddress ? (
          // Shown as text, as the deletion page shows it. A read-only field
          // looked editable and took the focus, and typing into it did
          // nothing.
          <div className="signin-fixed">
            <div className="small muted">Email address</div>
            <div id="signin-address" className="signin-fixed-address">{fixedAddress}</div>
            <input id="signin-email" type="hidden" value={email} />
          </div>
        ) : (
          <>
            <label htmlFor="signin-email">Email address</label>
            <input
              id="signin-email"
              type="email"
              autoFocus={autoFocus}
              autoComplete="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </>
        )}
        {error && <div className="notice notice-bad signin-notice">{error}</div>}
        {/* Not disabled on an empty field. With Google beside it, the page
            loaded with the filled primary inert and the live secondary looking
            identical — the only difference being that the one you could see
            was the one you could not press. `required` and type=email let the
            browser do the checking, at the moment somebody actually submits. */}
        <button id="continue-with-email" className="btn btn-block signin-submit" type="submit" disabled={busy}>
          {busy ? (
            <><span className="spinner" /> {enterprise ? 'Checking your address…' : 'Sending a code…'}</>
          ) : enterprise ? (
            'Continue'
          ) : (
            'Email me a code'
          )}
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={verify}>
      <p className="small muted signin-sent">
        If <strong>{email}</strong> can sign in to docsdone, a code is on
        its way. It expires in {expiresInMinutes} minutes.
      </p>
      <label htmlFor="signin-code">Six-digit code</label>
      <input
        id="signin-code"
        // Not type="number": a code is a string of six digits, and a number
        // input drops a leading zero and offers a spinner to increment it.
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        pattern="[0-9]{6}"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      />
      <TermsBox termsUrl={termsUrl} checked={acceptTerms} onChange={setAcceptTerms} />
      {error && <div className="notice notice-bad signin-notice">{error}</div>}
      <button className="btn btn-block signin-submit" type="submit" disabled={busy || code.length !== 6 || !acceptTerms}>
        {busy ? <><span className="spinner" /> Signing in…</> : 'Sign in'}
      </button>
      {!fixedAddress && (
        <p className="small faint signin-back">
          <button className="btn btn-link" type="button" disabled={busy} onClick={() => { setExpiresInMinutes(0); setError('') }}>
            Use a different address
          </button>
        </p>
      )}
    </form>
  )
}
